// Motion-over-html renderer branch (Lenses v1.1).
//
// A `type: motion-over-html` lens is fully self-contained: its
// `scratch/render.mjs` owns both the synthesis (subject + variables →
// animation HTML) and the deterministic capture (headless Chromium frame
// seek → ffmpeg mp4). The daemon's job is only to invoke that script with
// the validated run inputs and stream the resulting video back as a
// data-URI — no external generation API, no Magnific key involved.
//
// Contract with the lens script (see the appostle-create-new-lens skill,
// references/type-motion-over-html.md):
//
//   node scratch/render.mjs --subject <text> --variables <json> --out <file.mp4>
//
// The script must exit 0 with the mp4 written to --out. Geometry/timing
// (aspect width/height, fps, beat lengths) are the script's business — it
// reads them off the variables JSON and the lens frontmatter.

import { spawn } from "node:child_process";
import { mkdir, readFile, stat, appendFile } from "node:fs/promises";
import { randomBytes } from "node:crypto";
import { join } from "path";
import type pino from "pino";
import { resolveLensDir } from "./paths.js";
import { isValidUploadId, readLensInputSpecs, uploadsDir } from "./uploads.js";

const MOTION_RENDER_TIMEOUT_MS = 3 * 60 * 1000;

export interface RunMotionRenderArgs {
  cwd: string;
  lensSlug: string;
  subject: string;
  variables: Record<string, string>;
  inputs?: Record<string, string>;
  clientUserId: string;
  logger: pino.Logger;
  onProgress: (stage: "posted" | "polling" | "downloading" | "saved", detail?: string) => void;
}

export interface RunMotionRenderResult {
  ok: boolean;
  imageUrl?: string;
  imageSlug?: string;
  error?: string;
  errorCode?: string;
}

function todayFolder(): string {
  const d = new Date();
  const yyyy = d.getUTCFullYear();
  const mm = String(d.getUTCMonth() + 1).padStart(2, "0");
  const dd = String(d.getUTCDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

async function appendPerLensLog(
  cwd: string,
  lensSlug: string,
  entry: Record<string, unknown>,
): Promise<void> {
  const dir = join(resolveLensDir(cwd, lensSlug), "renders");
  try {
    await mkdir(dir, { recursive: true });
    await appendFile(join(dir, "log.jsonl"), `${JSON.stringify(entry)}\n`);
  } catch {
    // History is best-effort; never fail the render over it.
  }
}

export async function runMotionRender(args: RunMotionRenderArgs): Promise<RunMotionRenderResult> {
  const { cwd, lensSlug, subject, variables, clientUserId, logger, onProgress } = args;
  const log = logger.child({ component: "lens-motion", lensSlug });

  const lensPath = resolveLensDir(cwd, lensSlug);
  const scriptPath = join(lensPath, "scratch", "render.mjs");
  try {
    await stat(scriptPath);
  } catch {
    return {
      ok: false,
      error: "motion lens has no scratch/render.mjs",
      errorCode: "motion_scaffold_missing",
    };
  }

  const inputSpecs = await readLensInputSpecs(cwd, lensSlug);
  if (inputSpecs === null) {
    return { ok: false, error: "lens file not found", errorCode: "lens_not_found" };
  }
  const inputPaths: Record<string, string> = {};
  for (const spec of inputSpecs) {
    if (spec.required && !args.inputs?.[spec.key]) {
      return {
        ok: false,
        error: `missing required input: ${spec.key}`,
        errorCode: "client_input_missing",
      };
    }
  }
  for (const [key, uploadId] of Object.entries(args.inputs ?? {})) {
    if (!inputSpecs.some((spec) => spec.key === key)) {
      return {
        ok: false,
        error: `lens does not declare input "${key}"`,
        errorCode: "client_input_invalid",
      };
    }
    if (!isValidUploadId(uploadId)) {
      return {
        ok: false,
        error: `invalid uploadId for input ${key}`,
        errorCode: "client_input_invalid",
      };
    }
    const path = join(uploadsDir(cwd, lensSlug), uploadId);
    try {
      await readFile(path);
    } catch {
      return {
        ok: false,
        error: `upload not found for input ${key} — upload it again`,
        errorCode: "client_input_missing",
      };
    }
    inputPaths[key] = path;
  }

  const outDir = join(lensPath, "renders", todayFolder());
  const imageSlug = `${lensSlug}-motion-${randomBytes(4).toString("hex")}`;
  const outPath = join(outDir, `${imageSlug}.mp4`);
  try {
    await mkdir(outDir, { recursive: true });
  } catch (err) {
    log.error({ err, outDir }, "failed to create renders dir");
    return { ok: false, error: "failed to create output dir", errorCode: "motion_render_failed" };
  }

  onProgress("posted");
  await appendPerLensLog(cwd, lensSlug, {
    ts: new Date().toISOString(),
    clientUserId,
    lens: lensSlug,
    inputs: { subject, variables },
    status: "posted",
  });

  const stderrTail: string[] = [];
  const exitCode = await new Promise<number | null>((resolve) => {
    const child = spawn(
      process.execPath,
      [
        scriptPath,
        "--subject",
        subject,
        "--variables",
        JSON.stringify(variables),
        "--inputs",
        JSON.stringify(inputPaths),
        "--out",
        outPath,
      ],
      { cwd: lensPath, env: process.env, stdio: ["ignore", "ignore", "pipe"] },
    );
    const timer = setTimeout(() => {
      child.kill("SIGKILL");
    }, MOTION_RENDER_TIMEOUT_MS);
    child.stderr.on("data", (chunk: Buffer) => {
      stderrTail.push(chunk.toString());
      if (stderrTail.length > 20) stderrTail.shift();
    });
    child.on("error", () => {
      clearTimeout(timer);
      resolve(null);
    });
    child.on("close", (code) => {
      clearTimeout(timer);
      resolve(code);
    });
  });
  onProgress("polling");

  if (exitCode !== 0) {
    const detail = stderrTail.join("").trim().slice(-400);
    log.error({ exitCode, detail }, "motion render script failed");
    await appendPerLensLog(cwd, lensSlug, {
      ts: new Date().toISOString(),
      clientUserId,
      lens: lensSlug,
      inputs: { subject, variables },
      status: "error",
      error: detail || `render script exit ${exitCode}`,
    });
    return {
      ok: false,
      error: detail || `render script exit ${exitCode ?? "spawn-failed"}`,
      errorCode: "motion_render_failed",
    };
  }

  let bytes: Buffer;
  try {
    bytes = await readFile(outPath);
  } catch (err) {
    log.error({ err, outPath }, "render script exited 0 but wrote no output");
    return {
      ok: false,
      error: "render produced no output file",
      errorCode: "motion_render_failed",
    };
  }

  onProgress("saved", outPath);
  await appendPerLensLog(cwd, lensSlug, {
    ts: new Date().toISOString(),
    clientUserId,
    lens: lensSlug,
    inputs: { subject, variables },
    status: "saved",
    file: outPath,
  });
  log.info({ outPath, bytes: bytes.length }, "motion render saved");

  return {
    ok: true,
    imageUrl: `data:video/mp4;base64,${bytes.toString("base64")}`,
    imageSlug,
  };
}
